import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Clock, CheckCircle, AlertCircle, XCircle, Loader2, FileText,
  Car, Search, Plus, ChevronRight,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_MAP = {
  pending_review: { label: 'Pendiente', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock },
  in_progress: { label: 'En proceso', color: 'bg-blue-500/20 text-blue-400', icon: AlertCircle },
  completed: { label: 'Completado', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  delivered: { label: 'Entregado', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  cancelled: { label: 'Cancelado', color: 'bg-red-500/20 text-red-400', icon: XCircle },
  rejected: { label: 'Rechazado', color: 'bg-red-500/20 text-red-400', icon: XCircle },
};

const TicketsListPage = () => {
  const { token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const res = await axios.get(`${API}/tickets`, { headers: { Authorization: `Bearer ${token}` } });
        setTickets(res.data.tickets || []);
      } catch { toast.error(t('Error al cargar solicitudes')); }
      finally { setLoading(false); }
    };
    fetchTickets();
  }, [token]);

  const filtered = tickets.filter(t => {
    if (!search.trim()) return true;
    const q = search.toLowerCase().trim();
    return (
      (t.ticket_number || '').toLowerCase().includes(q) ||
      (t.license_plate || '').toLowerCase().includes(q) ||
      (t.vehicle_brand || '').toLowerCase().includes(q) ||
      (t.vehicle_model || '').toLowerCase().includes(q) ||
      (t.ecu_type || '').toLowerCase().includes(q) ||
      (t.services_requested || []).some(s => s.toLowerCase().includes(q))
    );
  });

  if (loading) return (
    <Layout><div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div></Layout>
  );

  return (
    <Layout>
      <div className="max-w-5xl mx-auto" data-testid="tickets-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white">{t('MIS SOLICITUDES')}</h1>
            <p className="text-zinc-400 mt-1">{tickets.length} {tickets.length !== 1 ? t('solicitudes') : t('solicitud')}</p>
          </div>
          <Button onClick={() => navigate('/new-request')} className="bg-primary hover:bg-primary/90" data-testid="new-request-btn">
            <Plus className="w-4 h-4 mr-2" />{t('Nueva Solicitud')}
          </Button>
        </div>

        {/* Search */}
        <div className="relative mb-6" data-testid="tickets-search">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder={t('Buscar por matrícula, vehículo, ECU, servicio o nº ticket...')}
            className="pl-10 bg-surface border-white/10 text-white placeholder:text-zinc-500"
            data-testid="tickets-search-input"
          />
        </div>

        {/* Tickets List */}
        {filtered.length === 0 ? (
          <div className="bg-surface border border-white/10 rounded-md p-12 text-center">
            <FileText className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
            <h3 className="text-white font-heading font-bold mb-2">
              {search ? t('SIN RESULTADOS') : t('NO HAY SOLICITUDES')}
            </h3>
            <p className="text-zinc-500 text-sm">
              {search ? `${t('No se encontraron solicitudes para')} "${search}"` : t('Crea tu primera solicitud para empezar')}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map((tk) => {
              const st = STATUS_MAP[tk.status] || STATUS_MAP.pending_review;
              return (
                <div
                  key={tk.id}
                  className="bg-surface border border-white/10 rounded-md p-4 hover:border-white/20 cursor-pointer transition-colors group"
                  onClick={() => navigate(`/tickets/${tk.id}`)}
                  data-testid={`ticket-row-${tk.ticket_number}`}
                >
                  <div className="flex items-center gap-4">
                    {/* Status icon */}
                    <div className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center ${st.color}`}>
                      <st.icon className="w-5 h-5" />
                    </div>

                    {/* Main info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-white font-heading font-bold text-sm">{tk.ticket_number}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${st.color}`}>{t(st.label)}</span>
                        {tk.license_plate && (
                          <span className="text-xs px-2 py-0.5 rounded bg-zinc-800 text-zinc-300 font-mono font-bold tracking-wider" data-testid="ticket-plate">
                            {tk.license_plate.toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 text-sm text-zinc-400">
                        <Car className="w-3 h-3 flex-shrink-0" />
                        <span className="truncate">
                          {tk.vehicle_brand} {tk.vehicle_model} {tk.vehicle_year ? `(${tk.vehicle_year})` : ''}
                        </span>
                        {tk.ecu_type && (
                          <span className="text-zinc-600">|</span>
                        )}
                        {tk.ecu_type && (
                          <span className="text-zinc-500 font-mono text-xs">{tk.ecu_type}</span>
                        )}
                      </div>
                      {/* Services */}
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {(tk.services_requested || []).map(s => (
                          <span key={s} className="text-[10px] px-1.5 py-0.5 rounded bg-primary/10 text-primary font-mono uppercase">
                            {s}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Date + arrow */}
                    <div className="flex-shrink-0 text-right hidden sm:block">
                      <p className="text-zinc-500 text-xs">
                        {new Date(tk.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                      </p>
                      <p className="text-zinc-600 text-xs">
                        {new Date(tk.created_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                    <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors flex-shrink-0" />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default TicketsListPage;
